import { FC, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { SPACE_FIELD_META } from "@leopart/shared/contracts/space.contract";
import type { SpaceCreateInput } from "@leopart/shared/contracts/space.contract";
import CustomButton from "../../shared/CustomButton/CustomButton";
import CustomRadioInput from "../../shared/CustomRadioInput/CustomRadioInput";
import { ISpace } from "../../models/Space";
import CustomTabsV3 from "../../shared/CustomTabsV3/CustomTabsV3";
import style from "./AddSpaceModa.module.scss";
import { TypeSpaceIcon } from "../../shared/Icons/Icons";
import Typhography from "../../shared/Typhography/Typhography";
import GenericModalV2 from "../../shared/GenericModal/GenericModal";
import { CustomFormV3 } from "../../shared/CustomFormV3/CustomForm";
import { CustomFieldDef, FieldDef, SelectOption } from "../../shared/CustomFormV3/types";
import { useAddSpaceModal } from "./useAddSpaceModal";

const tabs = ["Informazioni", "Impostazioni"] as const;

/**
 * Modal Spazio CONTRACT-DRIVEN: campi, label, options e validazione derivano
 * da `spaceCreateSchema` + `SPACE_FIELD_META` (@leopart/shared) via
 * `zodToFieldDefs` — vedi useAddSpaceModal. Qui solo presentazione.
 */
export const AddSpaceModal: FC<{
  onClose: () => void;
  onDelete: () => void;
  onConfirm: (form: SpaceCreateInput) => void | Promise<void>;
  isOpen?: boolean | ISpace;
  isLoading: boolean;
}> = ({ isOpen, onClose, isLoading, onConfirm, onDelete }) => {
  const {
    methods,
    baseFieldDefs,
    isAddMode,
    currentTab,
    setCurrentTab,
    isSaveDisabled,
    submit,
    handleDeleteSpace,
  } = useAddSpaceModal({ isOpen, onClose, onDelete, onConfirm });

  // Adattamenti di sola PRESENTAZIONE dei FieldDef generati dal contract:
  // - `type`: non modificabile in edit (comportamento storico del modal)
  // - `gender`: reso come radio (CustomRadioInput) per equivalenza visiva,
  //   con le stesse option/label generate dal contract
  const layout = useMemo<FieldDef[]>(
    () =>
      baseFieldDefs.map((def) => {
        if (def.key === "type") return { ...def, disabled: !isAddMode };
        if (def.key === "gender" && def.type === "single-select") {
          const options = def.options as SelectOption[];
          const genderField: CustomFieldDef = {
            key: "gender",
            type: "custom",
            size: 12,
            render: (field) => (
              <CustomRadioInput
                label={SPACE_FIELD_META.fields.gender.label}
                value={field.value}
                onChange={field.onChange}
                options={options}
              />
            ),
          };
          return genderField;
        }
        return def;
      }),
    [baseFieldDefs, isAddMode]
  );

  const renderForm = () => {
    return (
      <>
        {!isAddMode && renderInfo()}
        <div className="d-flex flex-column gap-3">
          <CustomFormV3 layout={layout} gridProps={{ gap: 4 }} />
          <div className="d-flex justify-content-end gap-2 mt-3">
            <CustomButton
              disabled={isLoading}
              color="outline"
              onClick={onClose}
            >
              Annulla
            </CustomButton>
            <CustomButton
              isLoading={isLoading}
              color="secondary"
              disabled={isSaveDisabled}
              onClick={submit}
            >
              Salva
            </CustomButton>
          </div>
        </div>
      </>
    );
  };

  const renderInfo = () => {
    return (
      <div className={style.infoContainer}>
        <div className="d-flex align-items-center gap-2">
          <TypeSpaceIcon
            className={style.typeIcon}
            type={(isOpen as ISpace)?.type}
          />
          <Typhography size="bold-14">{(isOpen as ISpace)?.name}</Typhography>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Typhography className={style.infoText} size="medium-12">
            {(isOpen as ISpace)?.teams?.length} Squadre
          </Typhography>
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <div className="d-flex flex-column gap-3">
        {renderInfo()}
        <div className={style.settingsContainer}>
          <div className="d-flex flex-column gap-2">
            <Typhography size="bold-16">Danger Zone</Typhography>
            <Typhography size="regular-14">
              Cancella definitivamente questo gruppo
            </Typhography>
          </div>
          <div className="d-flex align-items-center">
            <div
              className={style.textDangerContainer}
              onClick={handleDeleteSpace}
            >
              <Typhography size="bold-12" className={style.textDanger}>
                Cancella gruppo
              </Typhography>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTab = () => {
    if (isAddMode) return null;
    return {
      0: renderForm(),
      1: renderSettings(),
    }[currentTab];
  };

  const renderEditContent = () => {
    return (
      <div className="d-flex flex-column gap-2">
        <CustomTabsV3
          tabs={tabs.map((tab, i) => ({ label: tab, value: String(i) }))}
          value={String(currentTab)}
          onValueChange={(e) => setCurrentTab(Number(e.value))}
        ></CustomTabsV3>
        {renderTab()}
      </div>
    );
  };

  return (
    <FormProvider {...methods}>
      <GenericModalV2
        isLoading={isLoading}
        size={"lg"}
        onClose={onClose}
        isOpen={!!isOpen}
        title={`${isAddMode ? "Nuovo" : "Modifica"} Spazio`}
      >
        {isAddMode ? renderForm() : renderEditContent()}
      </GenericModalV2>
    </FormProvider>
  );
};

export default AddSpaceModal;
